import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

import SubHeader from "components/Navbars/SubHeader";
import { useChangePassword, useGetAccount } from "../../api/account/service";
import { getActiveColorStyle } from "../../utils/colorUtils";

const ChangePassword = ({
  activeColor = "secondary",
  shellThemeVariant = "1",
  isLightTheme = false,
}) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const isTheme2 = shellThemeVariant === "2";
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 992
  );

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const contentClassName = classNames("content", "change-password-page", {
    "theme2-admin-content-compact": isTheme2,
    "theme2-change-password-inset": isTheme2,
  });

  const changePasswordMutation = useChangePassword();
  const { isLoading: accountLoading } = useGetAccount();

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("New passwords do not match!");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert("New password must be at least 6 characters!");
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword: passwordData.currentPassword,
        password: passwordData.newPassword,
        passwordConfirmation: passwordData.confirmPassword,
      });
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      console.error("Password change error:", error);
    }
  };

  if (accountLoading) {
    return (
      <div
        className={contentClassName}
        style={{
          minHeight: "80vh",
          paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
        }}
      >
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
          <div className="spinner-border" role="status" style={{ color: activeColorStyle.borderColor }}>
            <span className="sr-only">Loading...</span>
          </div>
          <span className="ml-2" style={{ color: activeColorStyle.borderColor }}>
            Loading user info...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={contentClassName}
      style={{
        minHeight: "80vh",
        paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
      }}
    >
      <div style={{ width: "100%" }}>
        <div className="change-password-subheader-wrap">
          <SubHeader
            activeColor={activeColor}
            back={-1}
            title={t("routes.changePassword")}
            theme2ScrollGapFill={isTheme2}
            isLightTheme={isLightTheme}
          />
        </div>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <Form onSubmit={handlePasswordSubmit}>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="currentPassword">Current Password</Label>
                        <Input
                          type="password"
                          name="currentPassword"
                          id="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          required
                          placeholder="Enter your current password"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="newPassword">New Password</Label>
                        <Input
                          type="password"
                          name="newPassword"
                          id="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          required
                          minLength={6}
                          placeholder="Enter your new password (min 6 characters)"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="confirmPassword">Confirm New Password</Label>
                        <Input
                          type="password"
                          name="confirmPassword"
                          id="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                          minLength={6}
                          placeholder="Re-enter your new password"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6" className="d-flex align-items-center justify-content-end">
                      <Button
                        style={{ ...activeColorStyle, color: "white" }}
                        type="submit"
                        disabled={changePasswordMutation.isPending}
                      >
                        {changePasswordMutation.isPending ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm mr-2"
                              role="status"
                              aria-hidden="true"
                            />
                            Changing...
                          </>
                        ) : (
                          <>
                            <i className="tim-icons icon-lock-circle mr-1" />
                            Change Password
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ChangePassword;
